import { Button } from "components/Button";
import { useCampaignsStore } from "screens/Campaigns/store";
import { SquareLoader } from "screens/Main/components/Loader/SquareLoader";
import { TasksAddForm } from "../../TasksAddForm";
import { useEffect, useState, useCallback } from "react";
import { campaignsApi } from "screens/Campaigns/service";
import { EditIcon } from "assets/icons/Edit";
import { ETaskType, ITask } from "screens/Campaigns/types";
import { TwitterIcon } from "assets/icons/Campaigns/TwitterIcon";
import { RepostIcon } from "assets/icons/Campaigns/RepostIcon";
import { WebsiteIcon } from "assets/icons/Campaigns/WebsiteIcon";
import TrashIcon from "assets/icons/TrashIcon";

export const TasksPanel = () => {
  const { TASKS, setProjects } = useCampaignsStore();
  const {
    totalPages,
    page,
    setPage,
    tasks,
    isOpenAddTaskForm,
    setIsOpenAddTaskForm,
    setPageSize,
    pageSize,
    setTasks,
    setSelectedTask,
    setTotalPages
  } = TASKS;
  const [isLoading, setIsLoading] = useState(false);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
  };

  const getAllTasksHandler = useCallback(async () => {
    setIsLoading(true);
    try {
      const { total, data } = await campaignsApi.getTasks(page, pageSize);
      const projects = await campaignsApi.getAllProjects(1);

      if (projects) {
        setProjects(projects);
      }

      if (data) {
        setTasks(data);
      }
      setTotalPages(total);
    } catch (error) {
      console.error("Error loading tasks data", error);
    } finally {
      setIsLoading(false);
    }
  }, [setProjects, setTasks, page, pageSize]);

  const handleEditTask = (task: ITask) => {
    setSelectedTask(task);
    setIsOpenAddTaskForm(true);
  };

  const handleDeleteTask = async (id: string) => {
    setIsLoading(true);
    await campaignsApi.deleteTask(id);
    await getAllTasksHandler();
    setIsLoading(false);
  };

  useEffect(() => {
    getAllTasksHandler();
  }, [getAllTasksHandler, page, pageSize]);

  const getTaskIcon = (type: ETaskType | null) => {
    switch (type) {
      case ETaskType.FOLLOW:
        return <TwitterIcon />;
      case ETaskType.REPOST:
        return <RepostIcon />;
      case ETaskType.VISIT:
        return <WebsiteIcon />;
      default:
        break;
    }
  };

  return (
    <>
      <Button
        extraClass="flex items-center justify-center justify-self-center mb-[60px] min-w-[192px]"
        onClick={() => {
          setSelectedTask(null);
          setIsOpenAddTaskForm(true);
        }}
      >
        <span className="mr-[10px] text-[18px]">+</span> Add new tasks
      </Button>
      <div className="w-full">
        <p className="font-[700] mb-[24px]">All tasks ({tasks.length})</p>
        {isLoading ? (
          <div className="flex w-full my-[20px] justify-center items-center">
            <SquareLoader size={32} />
          </div>
        ) : (
          <table className="min-w-full table-auto">
            <thead>
              <tr className="bg-gray-500">
                <th className="px-4 py-2 text-center">Project Title</th>
                <th className="px-4 py-2 text-center">Type</th>
                <th className="px-4 py-2 text-left">Task title</th>
                <th className="px-4 py-2 text-center">Order</th>
                <th className="px-4 py-2 text-left">Source</th>
                <th className="px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks
                .sort((taskA, taskB) => taskA.order - taskB.order)
                .map((task, index) => (
                  <tr key={index} className="border-b">
                    <td className="px-4 py-2">
                      <div className="flex flex-col items-center">
                        <img
                          src={task.project.project_url}
                          alt="project-logo"
                          className="w-6 h-6 overflow-hidden rounded-full object-contain"
                        />
                        <p className='text-center'>{task.project.title}</p>
                      </div>
                    </td>
                    <td className="px-4 py-2 capitalize">
                      <div className="flex justify-center">
                        {getTaskIcon(task.type)}
                      </div>
                    </td>
                    <td className="px-4 py-2">{task.title}</td>
                    <td className="px-4 py-2 text-center">{task.order}</td>
                    <td className="px-4 py-2">
                      {task.source ? (
                        <a href={task.source} target="_blank" rel="noreferrer">
                          {task.source}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          className="p-2 bg-[#FFFFFF1A] rounded-full"
                          onClick={() => handleEditTask(task)}
                        >
                          <EditIcon width={14} height={14} />
                        </button>
                        <button
                          className="p-2 bg-[#FFFFFF1A] rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            task.id && handleDeleteTask(task.id);
                          }}
                        >
                          <TrashIcon width={16} height={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg disabled:bg-gray-300 disabled:text-black"
          >
            Previous
          </button>

          <span className="text-lg">
            Page {page} of {Math.ceil(totalPages / pageSize)}
          </span>

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page * pageSize >= totalPages}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg disabled:bg-gray-300 disabled:text-black"
          >
            Next
          </button>
        </div>

        <div className="mt-4 flex text-white justify-end">
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="px-4 py-2 border rounded-lg text-white focus:outline-none"
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
      </div>
      <TasksAddForm
        isOpen={isOpenAddTaskForm}
        onClose={() => {
          setIsOpenAddTaskForm(false);
          setSelectedTask(null);
        }}
        getAllTasksHandler={getAllTasksHandler}
      />
    </>
  );
};
